import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../Avatar';
import StageChip from './StageChip';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import type { DashboardPatient } from '../../services/patient';

const MAX_RESULTS = 8;

interface PatientPickerProps {
    /**
     * The practice's full patient list, straight from `usePatientList()` (the
     * same TanStack Query-backed provider the Home page table reads from).
     * That provider fetches `GET /patients/mine` unfiltered; the Home page's
     * search bar and status/action/practice dropdowns only filter a local
     * derived array inside PatientTable.tsx and never write back into the
     * provider, so this list is intentionally NOT scoped by any of that UI
     * state.
     */
    patients: DashboardPatient[];
    selectedPatientId: string | null;
    onSelect: (patientId: string) => void;
}

const PatientPicker: React.FC<PatientPickerProps> = ({ patients, selectedPatientId, onSelect }) => {
    const { getProfilePictureUrl, getProfileThumbnailUrl } = usePatientMetadata();
    const [query, setQuery] = useState('');
    const [picking, setPicking] = useState(!selectedPatientId);

    const selectedPatient = useMemo(
        () => patients.find((p) => p.id === selectedPatientId) ?? null,
        [patients, selectedPatientId]
    );

    const matches = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) return patients;
        return patients.filter((p) => p.name.toLowerCase().includes(trimmed));
    }, [patients, query]);

    const results = useMemo(() => matches.slice(0, MAX_RESULTS), [matches]);
    const hasMoreMatches = matches.length > MAX_RESULTS;

    if (selectedPatient && !picking) {
        return (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5">
                <div className="h-8 w-8 shrink-0">
                    <Avatar
                        user={{ name: selectedPatient.name, email: selectedPatient.id }}
                        imageURL={getProfilePictureUrl(selectedPatient.id)}
                        thumbnailURL={getProfileThumbnailUrl(selectedPatient.id) ?? undefined}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-800 truncate">
                        {selectedPatient.name}
                    </div>
                    <div className="mt-1">
                        <StageChip stage={selectedPatient.treatmentStatus} />
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setPicking(true);
                        setQuery('');
                    }}
                    className="text-xs font-medium text-gray-500 hover:text-gray-800"
                >
                    Change
                </button>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 text-gray-400">
                <MagnifyingGlassIcon className="h-4 w-4" />
                <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by first or last name"
                    className="w-full text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                />
            </div>
            <div className="max-h-64 overflow-y-auto">
                {results.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-400">
                        No patients match &ldquo;{query}&rdquo;.
                    </div>
                )}
                {results.map((patient) => (
                    <button
                        type="button"
                        key={patient.id}
                        onClick={() => {
                            onSelect(patient.id);
                            setPicking(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left"
                    >
                        <div className="h-[26px] w-[26px] shrink-0">
                            <Avatar
                                user={{ name: patient.name, email: patient.id }}
                                imageURL={getProfilePictureUrl(patient.id)}
                                thumbnailURL={getProfileThumbnailUrl(patient.id) ?? undefined}
                            />
                        </div>
                        <span className="text-[13px] text-gray-800 flex-1 truncate">
                            {patient.name}
                        </span>
                        <StageChip stage={patient.treatmentStatus} />
                    </button>
                ))}
                {hasMoreMatches && (
                    <div className="px-3 py-2 text-[11px] text-gray-400 border-t border-gray-100">
                        Showing {results.length} of {matches.length} patients. Keep typing to narrow
                        the list.
                    </div>
                )}
            </div>
        </div>
    );
};

export default PatientPicker;
